/**
 * Guía: Campañas.
 *
 * Contenido derivado del capítulo **31** del manual: §0 (aquí 121Pass regala
 * su propio dinero), §2 (el ciclo y por qué activar es el punto sin vuelta
 * atrás) y §3 (la distinción entre quién recibe el bono y dónde puede
 * gastarse).
 *
 * Es la pantalla más cara del panel por descuido, y la guía lo dice en el
 * primer paso: un formulario mal rellenado y activado compromete gasto real
 * e irreversible.
 */

import type { TourFlow } from '../types';

// Regalos › Bonos en dinero › Campañas (antes /campaigns).
const CAMPANAS_PATH = '/gifts/bonos/campanas';

export const c31Campanas: TourFlow = {
  id: 'admin.campaigns',
  chapter: '31',
  title: 'Campañas',
  summary:
    'Bonos que absorbe 121Pass: por qué activar no tiene vuelta atrás y dónde se puede gastar cada bono.',
  href: CAMPANAS_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'Aquí la plataforma pone su propio dinero',
      body:
        'El resto de Finanzas mueve dinero de otros. Un bono de campaña lo absorbe ' +
        '121Pass: el cliente compra con él y el comercio cobra el 100 % al liquidar. Es el ' +
        'mecanismo de «los primeros 500 registros se llevan un saldo».',
      route: CAMPANAS_PATH,
    },
    {
      id: 'draft-is-free',
      title: 'En borrador no hay gasto',
      body:
        'Todo es editable mientras siga en borrador. Es donde se revisa el importe, el cupo ' +
        'y el alcance — porque después ya no se tocan.',
      target: 'admin.campaigns.statusTabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'activate-is-final',
      title: 'Activar es el punto sin vuelta atrás',
      body:
        'A partir de ahí el importe y el cupo quedan bloqueados y cada registro que cumpla ' +
        'la condición consume cupo. Cada concesión guarda su importe, así que editar la ' +
        'campaña después **no toca lo ya otorgado**: lo dado, dado.',
    },
    {
      id: 'coherence-gate',
      title: 'No deja activar una campaña incoherente',
      body:
        'Una con alcance por comercios y ningún comercio elegido, una de organización sin ' +
        'organización, o una con el cupo ya agotado. Si el botón no responde como esperas, ' +
        'es una de esas tres.',
    },
    {
      id: 'trigger-vs-scope',
      title: 'Quién lo recibe y dónde se gasta son cosas distintas',
      body:
        'El disparador decide **a quién** se le da el bono. El alcance decide **dónde** ' +
        'puede gastarlo: en todos los comercios, en una lista concreta o en los de una ' +
        'organización. Confundirlos es la causa de «di el bono a quien no era» y de «no ' +
        'me lo acepta en esta tienda».',
      target: 'admin.campaigns.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'scope-resolved-on-spend',
      title: 'El límite se comprueba al pagar',
      body:
        'El bono entra a la billetera sin llevar su restricción escrita: se resuelve en ' +
        'el momento de gastarlo. Por eso un cliente puede ver saldo de campaña y que no le ' +
        'aplique en un comercio concreto, y es correcto.',
    },
    {
      id: 'pause-vs-finish',
      title: 'Pausar se revierte; finalizar, no',
      body:
        'Una campaña pausada se reanuda. Una finalizada no vuelve — y también se finaliza ' +
        'sola cuando se agota el cupo. Para parar el gasto un momento, pausar.',
    },
    {
      id: 'country-scope',
      title: 'Sólo tu país',
      body:
        'Listar, editar, activar y ver la analítica se resuelven contra el país de tu ' +
        'ficha, y un borrador no puede moverse a otro país. Activar en el mercado ' +
        'equivocado sería comprometer un gasto que no es tuyo.',
    },
  ],
};
